import { useMemo, useState } from "react";
import {
  App,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  List,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { addMeeting, updateMeeting } from "../../lib/api";
import type { WorkflowMeeting } from "../intakeDetails/types";

interface Props {
  intakeId: string;
  workflowId?: string;
  meetings: WorkflowMeeting[];
  onChanged: () => void;
}

export default function MeetingScheduler({
  intakeId,
  workflowId,
  meetings,
  onChanged,
}: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const sortedMeetings = useMemo(
    () => [...meetings].sort((a, b) => (a.meetingDate || "").localeCompare(b.meetingDate || "")),
    [meetings]
  );

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await addMeeting({
        intakeId,
        workflowId,
        meetingDate: values.meetingDate.toISOString(),
        meetingType: values.meetingType,
        attendees: (values.attendees || "")
          .split(/[\n,]/)
          .map((s: string) => s.trim())
          .filter(Boolean),
        notes: values.notes || "",
      });
      form.resetFields();
      message.success("Meeting scheduled");
      onChanged();
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const markCompleted = async (meeting: WorkflowMeeting) => {
    try {
      await updateMeeting(meeting.id, { status: "completed" });
      message.success("Marked completed");
      onChanged();
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title="Log initial meetings">
        <Form
          form={form}
          layout="vertical"
          initialValues={{ meetingType: "discovery" }}
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Date and time"
            name="meetingDate"
            rules={[{ required: true, message: "Pick a date and time" }]}
          >
            <DatePicker
              showTime
              style={{ width: "100%" }}
              aria-label="Meeting date and time"
            />
          </Form.Item>
          <Form.Item label="Meeting type" name="meetingType">
            <Select
              options={[
                { label: "Discovery", value: "discovery" },
                { label: "Scoping", value: "scoping" },
                { label: "Follow-up", value: "follow_up" },
                { label: "Vendor", value: "vendor" },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="Attendees (one per line or comma-separated)"
            name="attendees"
          >
            <Input.TextArea
              autoSize={{ minRows: 2 }}
              placeholder="alice@agency.gov&#10;bob@agency.gov"
            />
          </Form.Item>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea autoSize={{ minRows: 3 }} placeholder="Agenda, prep links, etc." />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Log meeting
          </Button>
        </Form>
      </Card>

      <Card title={`Scheduled meetings (${sortedMeetings.length})`}>
        {sortedMeetings.length === 0 ? (
          <Typography.Text type="secondary">
            No meetings scheduled yet.
          </Typography.Text>
        ) : (
          <List
            dataSource={sortedMeetings}
            renderItem={(meeting) => (
              <List.Item
                actions={
                  meeting.status !== "completed"
                    ? [
                        <Button
                          key="done"
                          type="link"
                          onClick={() => markCompleted(meeting)}
                        >
                          Mark completed
                        </Button>,
                      ]
                    : undefined
                }
              >
                <List.Item.Meta
                  title={
                    <Space wrap>
                      <span>
                        {meeting.meetingDate
                          ? dayjs(meeting.meetingDate).format("MMM D, YYYY h:mm A")
                          : "TBD"}
                      </span>
                      <Tag>{meeting.meetingType}</Tag>
                      <Tag
                        color={
                          meeting.status === "completed"
                            ? "success"
                            : meeting.status === "canceled"
                              ? "default"
                              : "processing"
                        }
                      >
                        {meeting.status}
                      </Tag>
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={2}>
                      {meeting.attendees?.length ? (
                        <span>Attendees: {meeting.attendees.join(", ")}</span>
                      ) : null}
                      {meeting.notes ? <span>{meeting.notes}</span> : null}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </Space>
  );
}
